import { useCallback, useEffect, useMemo, useState, type FC } from "react";

import { Button } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";

import AffiliatePamView from "./AffiliatePamView";
import {
  AFFILIATE_PAM_ACCOUNT_INITIAL_VALUES,
  DEFAULT_SELECTED_COLUMNS,
  KPI_ELIGIBLE_IDS,
  isPamAccountValid,
  type AffiliatePamAccountFormValues,
  type ShareableColumnId,
} from "./affiliatePamAccountData";

import styles from "./AffiliatePamAccount.module.css";

export type AffiliatePamAccountProps = {
  initialValues?: Partial<AffiliatePamAccountFormValues>;
  onValuesChange?: (values: AffiliatePamAccountFormValues) => void;
  onContinue?: (values: AffiliatePamAccountFormValues) => void;
};

const AffiliatePamAccount: FC<AffiliatePamAccountProps> = ({
  initialValues,
  onValuesChange,
  onContinue,
}) => {
  const seed = useMemo<ShareableColumnId[]>(() => {
    if (initialValues?.selectedColumns) return [...initialValues.selectedColumns];
    return [...AFFILIATE_PAM_ACCOUNT_INITIAL_VALUES.selectedColumns];
  }, [initialValues]);

  const [selectedColumns, setSelectedColumns] =
    useState<ShareableColumnId[]>(seed);

  // Push defaults up on mount so the parent picks them up even if the
  // operator never toggles a checkbox.
  useEffect(() => {
    onValuesChange?.({ selectedColumns: [...seed] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleColumn = useCallback(
    (id: ShareableColumnId) => {
      setSelectedColumns((current) => {
        const next = current.includes(id)
          ? current.filter((columnId) => columnId !== id)
          : [...current, id];
        onValuesChange?.({ selectedColumns: next });
        return next;
      });
    },
    [onValuesChange],
  );

  const handleContinue = useCallback(() => {
    onContinue?.({ selectedColumns: [...selectedColumns] });
  }, [onContinue, selectedColumns]);

  const isValid = isPamAccountValid({ selectedColumns });

  return (
    <div className={styles.step} data-testid="new-affiliate-pam-account">
      <header className={styles.intro}>
        <h2 className={styles.title}>
          {AppStrings.NEW_AFFILIATE_PAM_ACCOUNT_TITLE}
        </h2>
        <p className={styles.subtitle}>
          {AppStrings.NEW_AFFILIATE_PAM_ACCOUNT_SUBTITLE}
        </p>
      </header>

      <AffiliatePamView
        idPrefix="new-affiliate-pam-account"
        selectedColumns={selectedColumns}
        onToggleColumn={toggleColumn}
      />

      <div className={styles.divider} role="separator" aria-hidden="true" />

      <div className={styles.footer}>
        <Button
          intent="primary"
          disabled={!isValid}
          onClick={handleContinue}
          data-testid="new-affiliate-pam-account-continue"
        >
          {AppStrings.CONTINUE}
        </Button>
      </div>
    </div>
  );
};

export default AffiliatePamAccount;
export { DEFAULT_SELECTED_COLUMNS, KPI_ELIGIBLE_IDS };
